// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2014
//
// ## Rhythm Object
//
// An object for holding durations, beaming & tying information
// for notes, rests, etc...
//
// Use: add Rhythm as the property rhythm to any notational object
// to add rhythmic calculation to its rendering.
import type { RhythmicDuration, BaseDuration } from 'shared/definitions/musicTheory/rhythms';
import {
  assertValidBaseDuration,
  assertValidRhythmicDuration,
  rhythmicAliases,
  rhythmicFractions,
} from 'shared/definitions/musicTheory/rhythms';
import Fraction from './fraction';
import utils from '../utils';
import type { FontKey } from '../uTheory.Font/font';
import { toHTML } from '../uTheory.Font/font';
import Tuplet from './tuplet';
import type { AriaStringOptions } from './note';

class Rhythm {
  duration: RhythmicDuration; // base

  baseDuration: BaseDuration;

  dots: number;

  tuplets: Tuplet[];

  beaming: -1 | 0 | 1;

  /**
   *  Duration names:
   *  b, w, h, q, 8, 16, etc...
   *  Uses of 1, 2, 4 converted to w, h, q
   *  Use a period for each dot: q.
   *  Use t to indicate this note is tied from the previous note.
   * @prop {string} duration
   * @prop {Fraction} fraction representing duration w/ dots and tuplets
   * @prop {string} baseDuration 'w' for 'w.'. Duration without dots.
   * @prop {number} dots number of dots
   * @prop {Tuplet[]} tuplets applied to this rhythm
   * @prop {boolean} isTied
   */
  constructor(duration: string | number | Rhythm) {
    const whole = 'w';
    assertValidBaseDuration(whole);
    this.duration = whole; // default whole note
    this.baseDuration = whole;

    this.dots = 0;
    this._fraction = new Fraction(1, 1);

    this.tuplets = [];

    this.beaming = 0;
    // -1 : do not beam to next
    // 0 : default metric beaming
    // 1 : force beam to next

    // tied indicates that this note is the SECOND part of a tie
    // e.g.: don't articulate it in a rhythmic count.
    this._isTied = false;

    this.init(duration);
  }

  // Public Functions
  init(duration: string | number | Rhythm) {
    if (typeof duration !== 'undefined') this.setDuration(duration);
  }

  /**
   * Set the rhythm's duration
   * @param {string|number} duration
   */
  setDuration(duration: string | number | Rhythm) {
    duration = `${duration}`;
    // determine if it's tied:
    const tiedDuration = duration.replace(/t/g, '');
    // count to see if it's tied:
    this._isTied = duration.length !== tiedDuration.length;

    // remove the dots from the base duration:
    let baseDuration = tiedDuration.replace(/\./g, '');
    // count how many we removed:
    const dots = tiedDuration.length - baseDuration.length;

    // convert any aliases for base duration:
    if (baseDuration in rhythmicAliases) {
      baseDuration = rhythmicAliases[baseDuration as keyof typeof rhythmicAliases];
    }

    // make sure the base duration name is a valid name
    // and if it is, set the base duration.
    assertValidBaseDuration(baseDuration);

    this.baseDuration = baseDuration;
    // set the number of dots:
    this.dots = dots;
    // and update the fraction:
    this.updateFraction(baseDuration, dots);
    // and set the complete name:
    let durationWithDotsAndTie: string = baseDuration;
    for (let i = 0; i < this.dots; i++) {
      durationWithDotsAndTie += '.';
    }
    if (this._isTied) durationWithDotsAndTie += 't';
    assertValidRhythmicDuration(durationWithDotsAndTie);
    this.duration = durationWithDotsAndTie;
  }

  /**
   * @returns {string} duration including any dots
   */
  getDuration() {
    return this.duration;
  }

  /**
   * @returns {string} duration without any dots
   */
  getBaseDuration() {
    return this.baseDuration;
  }

  /**
   * Set the number of dots on a rhythm
   * @param {number} dots number of dots on note
   */
  setDots(dots: number) {
    if (utils.isNumeric(dots)) {
      this.dots = dots;
      this.updateFraction(this.baseDuration, this.dots);
    } else throw new Error('NaN: Dots must be expressed as a positive integer.');
    this.updateDuration();
  }

  /**
   * @returns {number} the number of dots on this rhythm
   */
  getDots() {
    return this.dots;
  }

  /**
   * Set [fraction] to the inputted time value
   * Set .dots to the inputted number of dots
   */
  private updateFraction(baseDuration: BaseDuration, dots: number) {
    const fractionString = rhythmicFractions[baseDuration];
    const fractionParts = fractionString.split('/');
    const numerator = Number(fractionParts[0]);
    const denominator = Number(fractionParts[1]);
    const fract = new Fraction(numerator, denominator);
    const dotFraction = fract.clone().multiply(1, 2);
    for (let i = 0; i < dots; i++) {
      fract.add(dotFraction);
      dotFraction.multiply(1, 2);
    }
    this._fraction = fract;
    this.dots = dots;
  }

  /**
   * Set .duration to the correct time value
   */
  private updateDuration() {
    let duration: string = this.baseDuration;
    for (let i = 0; i < this.dots; i++) duration += '.';
    if (this._isTied) duration += 't';
    assertValidRhythmicDuration(duration);
    this.duration = duration;
  }

  /**
   * A fraction representing duration, dots and any tuplets
   * @returns {Fraction}
   */
  getFraction(): Fraction {
    return this.fraction;
  }

  private _fraction: Fraction;

  /**
   * A fraction representing duration, dots and any tuplets
   * @returns {Fraction}
   */
  get fraction(): Fraction {
    return this._fraction.clone().multiply(this.getTupletFraction());
  }

  /**
   * Add tuplets to the rhythm
   */
  addTuplet(tuplet: Tuplet) {
    if (!(tuplet instanceof Tuplet)) {
      throw new Error(`TypeError: expected tuplet, got ${utils.typeof(tuplet)}`);
    }
    this.tuplets.push(tuplet);
  }

  /**
   * Remove the specified tuplet from the rhythm once
   */
  removeTuplet(tuplet: Tuplet) {
    if (!(tuplet instanceof Tuplet)) {
      throw new Error(`TypeError: expected tuplet, got ${utils.typeof(tuplet)}`);
    }
    const index = this.tuplets.indexOf(tuplet);
    if (index !== -1) this.tuplets.splice(index, 1);
  }

  /**
   * @returns {Fraction} representing combined effect of all tuplets
   */
  getTupletFraction() {
    const one = new Fraction(1, 1);
    // eslint-disable-next-line arrow-body-style
    return this.tuplets.reduce((accum, tuplet) => {
      return accum.divide(tuplet.ratio);
    }, one);
  }

  /**
   * Gets the base rhythm with no ties and dots
   */
  getBaseFraction() {
    const newR = new Rhythm(this.baseDuration);
    return newR.getFraction();
  }

  /**
   * Determine if rhythm can be beamed
   */
  isBeamable() {
    const eighth = new Fraction(1, 8);
    return this.getBaseFraction().lessThanEquals(eighth);
  }

  /**
   * Determine how many beam to give rhythm
   */
  numberOfBeams() {
    return this.getBaseFraction().denominator / 8;
  }

  /**
   * Add dots to rhythm
   */
  addDots(dots = 1) {
    if (!utils.isNumber(dots)) {
      throw new Error(`addDots can accept an integer or zero arguments, but not ${dots}`);
    }
    dots += this.dots;
    this.updateFraction(this.baseDuration, dots);
    this.updateDuration();
  }

  /**
   * Sets .beaming
   */
  setBeam(beamInteger: -1 | 0 | 1) {
    // -1 : no beam (break)
    // 0 : auto beaming
    // 1 : force beam
    this.beaming = beamInteger;
  }

  /**
   * Gets .beaming
   * -1 : no beam (break)
   * 0 : auto beaming
   * 1 : force beam
   */
  getBeam(): -1 | 0 | 1 {
    return this.beaming;
  }

  private _isTied: boolean = false;

  set isTied(bool) {
    this._isTied = !!bool;
    this.updateDuration();
  }

  get isTied(): boolean {
    return this._isTied;
  }

  setTie(tieBoolean: boolean) {
    this._isTied = !!tieBoolean;
    this.updateDuration();
  }

  getTie() {
    return this._isTied;
  }

  clone() {
    const newRhythm = new Rhythm(this.duration);
    newRhythm.setBeam(this.beaming);
    newRhythm.setTie(this._isTied);
    return newRhythm;
  }

  equals(thatRhythm: Rhythm | string) {
    thatRhythm = Rhythm.fromRhythmOrString(thatRhythm);
    return this.fraction.equals(thatRhythm.fraction);
  }

  toString() {
    return this.duration;
  }

  toAriaString(ariaStringOptions: AriaStringOptions = {}): string {
    ariaStringOptions = {
      rhythm: true,
      ...ariaStringOptions,
    };
    if (!ariaStringOptions.rhythm) return '';
    let text = '';
    if (this.dots) {
      if (this.dots > 1) {
        text += `${utils.tuple(this.dots)}-`;
      }
      text += 'dotted-';
    }
    text += this.toBaseDurationString(ariaStringOptions.british);
    return text;
  }

  toBaseDurationString(britishNames = false): string {
    switch (this.baseDuration) {
      case 'b':
        return britishNames ? 'breve' : 'breve';
      case 'w':
        return britishNames ? 'semibreve' : 'whole';
      case 'h':
        return britishNames ? 'minim' : 'half';
      case 'q':
        return britishNames ? 'crotchet' : 'quarter';
      case '8':
        return britishNames ? 'quaver' : 'eighth';
      case '16':
        return britishNames ? 'semiquaver' : 'sixteenth';
      case '32':
        return britishNames ? 'demisemiquaver' : '32nd';
      case '64':
        return britishNames ? 'hemidemisemiquaver' : '64th';
      case '128':
        return britishNames ? 'quasihemidemisemiquaver' : '128th';
      default:
        return utils.ordinal(Number(this.duration));
    }
  }

  /**
   * Format duration
   */
  toRhythmFontString() {
    const str = toHTML('rhythm', this.duration as FontKey);
    return `<span class='rhythm'>${str}</span>`;
  }

  /**
   * gets the fraction time value
   */
  value() {
    return this._fraction.valueOf();
  }

  /**
   * Create a rhythm from either a rhythm or a string
   */
  static fromRhythmOrString(rhythmOrString: Rhythm | string) {
    if (rhythmOrString instanceof Rhythm) return rhythmOrString;
    return new Rhythm(rhythmOrString);
  }
}

export default Rhythm;
